import React, { useContext, useEffect, useState } from 'react';
import { Flex, VStack, Text, Box, Divider } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SharedExperiencesContext } from '../../lib/sharedExperiencesProvider';
import { SharedExperiencePageContext } from '../lib/sharedExperiencePageContext';
import SharedExperienceListHeader from './sharedExperienceListHeader';
import ExperienceGroupInSharedExperienceListItem from './experienceGroupInSharedExperienceListItem';
import ExperiencesInSharedExperienceListItem from './experiencesInSharedExperienceListItem';
import { useSharedExperiencesApi } from '../../lib/useSharedExperiencesApi';
import SharedExperienceListMenu from './sharedExperienceListMenu';
import SharedExperienceTag from '@/components/sharedExperienceTag';
import useTranslation from '../../../lib/i18n/useTranslation';
export default function ExperiencesInSharedExperienceList() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const groupId = searchParams.get('group-id');
  const { rights, sharedExperience } = useContext(SharedExperiencesContext);
  const { 
    refreshExperienceList, 
    refreshSharedExperienceList,
    refresh,
    refreshTrigger, 
    experienceGroupList, 
    experienceList, 
    importedSharedExperienceGroups,
    importedSharedExperienceExperiences,
    isFetchingExperiences,
    group: currentGroup,
    parentGroup,
  } = useContext(SharedExperiencePageContext);
  const [localList, setLocalList] = useState(null);
  const [draggingItem, setDraggingItem] = useState(null);
  const sharedExperiencesApi = useSharedExperiencesApi();
  useEffect(() => {
    if (experienceList && experienceGroupList) {
      const filteredExperiences = currentGroup
        ? experienceList.filter(item => item.group_id === currentGroup.id)
        : experienceList.filter(item => !item.group_id);
      const filteredExperienceGroups = currentGroup
        ? experienceGroupList.filter(item => item.group_id === currentGroup.group.id)
        : experienceGroupList.filter(item => !item.group_id);
      let newLocalList = [
        ...filteredExperiences.map(experienceListItem => ({
          type: 'shared_experiences_experiences',
          data: experienceListItem,
          order: experienceListItem.experience_order || 1,
          id: experienceListItem.source_shared_experiences_id 
            ? 'shared_experience_imported_experience_' + experienceListItem.experience_shared_experience?.id
            : 'shared_experience_experience_' + experienceListItem.experience_shared_experience?.id,
          experiences_shared_experiences_id: experienceListItem.experience_shared_experience?.id,
          experience_id: experienceListItem.id,
          hover: false,
          area: null,
        })),
        ...filteredExperienceGroups.map(groupListItem => ({
          type: 'shared_experiences_group',
          data: groupListItem,
          order: groupListItem.order || 1,
          id: groupListItem.source_shared_experiences_id 
            ? 'shared_experience_imported_group-' + groupListItem.id
            : 'shared_experiences_group-' + groupListItem.id,
          group_id: groupListItem.id,
          hover: false,
          area: null,
        }))
      ];
      newLocalList = newLocalList.sort(compare);
      setLocalList(newLocalList);
    } else {
      setLocalList([]);
    }
  }, [experienceList, experienceGroupList, currentGroup]);
  function compare(a, b) {
    if (a.order < b.order)
      return -1;
    if (a.order > b.order)
      return 1;
    return 0;
  }
  const updateOrderLocally = (item, destination) => {
    let newLocalList;
    const source = item.order;
    
    if (destination < source) {
      newLocalList = localList.map((e) => {
        if (
          e.order < source
          && e.order >= destination
          && e.id !== item.id
        ) {
          e.order++
        } else if (e.id === item.id) {
          e.order = destination;
        }
        return e;
      })
    } else if (destination > source) {
      newLocalList = localList.map(e => {
        if (e.order > source
          && e.order <= destination
          && e.id !== item.id) {
          e.order--;
        } else if (e.id === item.id) {
          e.order = destination;
        }
        return e;
      })
    } else if (destination === source) {
      newLocalList = Array.from(localList);
    }
    newLocalList.forEach(newLocalListItem => {
      newLocalListItem.hover = false;
    })
    newLocalList.forEach(newLocalListItem => {
      newLocalListItem.area = null;
    })
    newLocalList = newLocalList.sort(compare);
    setLocalList(newLocalList);
  };
  const getItemId = (draggingItem) => {
    let itemId;
    if (draggingItem.type === 'shared_experiences_experiences') {
      itemId = draggingItem.experiences_shared_experiences_id;
    } else if (draggingItem.type === 'shared_experiences_group') {
      itemId = draggingItem.group_id;
    } else {
      itemId = draggingItem.group_id;
    }

    return itemId;
  };
  const orderAndMoveItems = async () => {

    try {
      if (!localList) {
        return;
      }
      let newLocalList = Array.from(localList);
      let lastItemHover;
      newLocalList.forEach((newLocalListItem) => {
        if (newLocalListItem.hover) {
          lastItemHover = newLocalListItem;
        }
      })
      
      if (lastItemHover && lastItemHover.id !== draggingItem.id) {
        if (lastItemHover.type === 'shared_experiences_group') {
          if (lastItemHover.area === 'middle') {
            if (draggingItem.type === 'shared_experiences_experiences') {
              await sharedExperiencesApi.moveExperienceToGroup(
                sharedExperience.id,
                draggingItem.data.id,
                lastItemHover.data.id
              );
              await refresh();
            } else if (draggingItem.type === 'shared_experiences_group') {
              await sharedExperiencesApi.moveGroupToGroup(
                draggingItem.group_id,
                lastItemHover.data.id,
                sharedExperience.id
              );
              await refresh();
            }
          } else {
            if (lastItemHover.area === 'border-top') {
              let newOrder;
              if (draggingItem.order < lastItemHover.order) {
                newOrder = lastItemHover.order - 1;
              } else {
                newOrder = lastItemHover.order;
              }

              await sharedExperiencesApi.updateOrderInSharedExperience(
                draggingItem.type,
                getItemId(draggingItem),
                newOrder
              );
              updateOrderLocally(draggingItem, newOrder);
            } else if (lastItemHover.area === 'border-bottom') {
              let newOrder;
              if (draggingItem.order > lastItemHover.order) {
                newOrder = lastItemHover.order + 1;
              } else {
                newOrder = lastItemHover.order;
              }

              await sharedExperiencesApi.updateOrderInSharedExperience(
                draggingItem.type,
                getItemId(draggingItem),
                newOrder
              );
              updateOrderLocally(draggingItem, newOrder);
            }
          }
        } else if (lastItemHover.type === 'shared_experiences_experiences') {
          
          if (lastItemHover.area === 'border-top') {
            let newOrder;
            if (draggingItem.order < lastItemHover.order) {
              newOrder = lastItemHover.order - 1;
            } else {
              newOrder = lastItemHover.order;
            }

            await sharedExperiencesApi.updateOrderInSharedExperience(
              draggingItem.type,
              getItemId(draggingItem),
              newOrder
            );
            updateOrderLocally(draggingItem, newOrder);
          } else if (lastItemHover.area === 'border-bottom') {
            let newOrder;
            if (draggingItem.order > lastItemHover.order) {
              newOrder = lastItemHover.order + 1;
            } else {
              newOrder = lastItemHover.order;
            }

            await sharedExperiencesApi.updateOrderInSharedExperience(
              draggingItem.type,
              getItemId(draggingItem),
              newOrder
            );
            updateOrderLocally(draggingItem, newOrder);
          }
        }
      } else {
        newLocalList.forEach(newLocalListItem => {
          newLocalListItem.hover = false;
        })
        newLocalList.forEach(newLocalListItem => {
          newLocalListItem.area = null;
        })
        newLocalList = newLocalList.sort(compare);
        setLocalList(newLocalList);
      }
    } catch (error) {
      console.error('❌ Error in orderAndMoveItems:', error)
    }
  }
  const onDragEnd = async () => {
    try {
      await orderAndMoveItems();
      setDraggingItem(null);
    } catch (error) {
      console.error(error)
    }
  }
  return (
    <>
      <Flex
        flexDirection="column"
        height={((localList && localList.length > 0) ? 'fit-content' : '100%')}
      >
        <SharedExperienceListHeader
          sharedExperience={sharedExperience}
          group={currentGroup}
          parentGroup={parentGroup}
          rights={rights}
          refreshList={async () => {
            await refresh();
            if (refreshSharedExperienceList && typeof refreshSharedExperienceList === 'function') {
              await refreshSharedExperienceList();
            }
          }}
          experienceList={experienceList}
          experienceGroupList={experienceGroupList}
        />
        <Divider />
        <Flex
          flexDirection="column"
          flex="1"
          overflow="auto"
        >
          {(!experienceGroupList || experienceGroupList.length === 0) && (!experienceList || experienceList.length === 0) ? (
            <Flex
              p={3}
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              gap={2}
              flex="1"
            >
              <Text color="text.medium.grey" fontSize="12px">
                {t('experience.noExperienceSoFar')}
              </Text>
              {rights?.can_update && (
                <SharedExperienceListMenu
                  hasExperiences={false}
                  group={currentGroup}
                  sharedExperienceId={sharedExperience.id}
                  onNew={async () => {
                    await refresh();
                  }}
                />
              )}
            </Flex>
          ) : (
            <Flex
              flexDirection="column"
              overflow="auto"
            >
              {localList && localList.map((item, i) => {
                return (
                  <Flex
                    flexDirection={"column"}
                    key={`${item.type}-${item.id}-${i}`}
                    onDragStart={() => {
                      setDraggingItem(item);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      
                      if (!draggingItem) return;
                      
                      let draggableItem;
                      if(e.target.draggable) {
                        draggableItem = e.target;
                      } else {
                        let iterableElement = e.target;
                        while(iterableElement.parentElement && !draggableItem) {
                          if(iterableElement.draggable) {
                            draggableItem = iterableElement
                          }
                          iterableElement = iterableElement.parentElement;
                        }
                      }
                      
                      if(draggableItem) {
                        const box = draggableItem.getBoundingClientRect();
                        let newArea;
                        
                        if (item.type === 'shared_experiences_experiences') {
                          newArea = e.clientY <= box.top + (box.height * 0.5) ? 'border-top' : 'border-bottom';
                        } else if (item.type === 'shared_experiences_group') {
                          if (e.clientY <= box.top + (box.height * (1/3))) {
                            newArea = 'border-top';
                          } else if (e.clientY >= box.top + (box.height * (2/3))) {
                            newArea = 'border-bottom';
                          } else {
                            newArea = 'middle';
                          }
                        }
                        
                        const currentItem = localList.find(listItem => listItem.id === item.id);
                        const needsUpdate = !currentItem?.hover || currentItem?.area !== newArea;
                        
                        if (needsUpdate) {
                          const newLocalList = localList.map((newLocalListItem) => {
                            if (newLocalListItem.id === item.id) {
                              return {
                                ...newLocalListItem,
                                hover: true,
                                area: newArea
                              };
                            } else {
                              return {
                                ...newLocalListItem,
                                hover: false,
                                area: null
                              };
                            }
                          });
                          setLocalList(newLocalList);
                        }
                      }
                    }}
                    onDragEnd={onDragEnd}
                    draggable="true"
                    onClick={(e) => {
                      if (item.type === 'shared_experiences_group') {
                        navigate(`/shared-experiences/${sharedExperience.id}?group-id=${item.data.id}`);
                      } else {
                        navigate(`/experiences/${item.data.id}`);
                      }
                    }}
                    cursor={"pointer"}
                  >
                    {(i > 0 && !item.hover) && <Divider style={{ pointerEvents: 'none' }} />}
                    {(item.hover && item.area === 'border-top') && <Divider style={{ pointerEvents: 'none' }} bg={"fill.green"} h={"2px"} />}
                    {item.type === 'shared_experiences_experiences' &&
                      <ExperiencesInSharedExperienceListItem
                        data={{
                          ...item.data,
                          id: item.data.id,
                          insights_count: item.data.insights_count,
                          personas: item.data.personas || [],
                          products: item.data.products || []
                        }}
                      />
                    }
                    {item.type === 'shared_experiences_group' &&
                      <ExperienceGroupInSharedExperienceListItem
                        dragHover={(item.hover && item.area === 'middle' && (draggingItem && (draggingItem.type === 'shared_experiences_experiences' || draggingItem.type === 'shared_experiences_group')))}
                        dragHoverEnable={rights.can_update}
                        data={item.data}
                      />
                    }

                    {(item.hover && item.area === 'border-bottom') && <Divider style={{ pointerEvents: 'none' }} bg={"fill.green"} h={"2px"} />}
                  </Flex>
                )
              })}
            </Flex>
          )}
        </Flex>
      </Flex>
    </>
  );
} 